import { createContext, ReactNode, useContext, useMemo } from 'react';
import {
  createPath,
  resolvePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Item } from '@asset-metrix/portal-ui';

import { NO_ROLE_ID } from '../../api/portalGatewayClient';
import { useStore } from '../../store';
import { pageWithRoleLink } from '../../utils/urlPath';

import { getActiveDynamicItemIndex, getActiveItemIndex } from './activeItems';
import { useDeleteAllNavigationCustomizationMutation } from './useDeleteAllNavigationCustomizationMutation';
import { useDeleteNavigationCustomizationMutation } from './useDeleteNavigationCustomizationMutation';
import { useGetStaticNavData } from './useFetchNavigationData';
import { useGetDynamicNavData } from './useNavigationCustomizationQuery';

export type NavigationContextType = {
  activeDynamicNavigationItemIndex?: number;
  activeMainItemIndex?: number;
  dynamicNavItems?: {
    href: string;
    iconName: string;
    label: string;
    sublabels?: string[];
    tooltip?: ReactNode;
  }[];
  initialPageSlug: string | null;
  navItems: { href: string; iconName: string; label: string }[];
  roleId?: string;
  handleClick: (item: Item) => void;
  handleClose: (itemId: string) => void;
  handleCloseAll: () => void;
  isPending: boolean;
};

export type NavigationContextProviderType = {
  children: ReactNode;
};

const NavigationContext = createContext<NavigationContextType | null>(null);

// Custom hook for navigation data
const useNavigationContextData = () => {
  const { '*': slug } = useParams();
  const location = useLocation();

  const {
    initialPageSlug,
    navItems,
    isPending: isStaticNavDataPending,
  } = useGetStaticNavData();

  const { navItems: dynamicNavItems, isPending: isDynamicNavDataPending } =
    useGetDynamicNavData();

  return useMemo(() => {
    const activeMainItemIndex =
      slug && navItems.length > 0
        ? getActiveItemIndex(navItems, slug)
        : undefined;

    const activeDynamicNavigationItemIndex =
      slug && location
        ? getActiveDynamicItemIndex(
            dynamicNavItems,
            location.pathname,
            location.search
          )
        : undefined;

    return {
      activeDynamicNavigationItemIndex,
      activeMainItemIndex,
      dynamicNavItems,
      initialPageSlug,
      navItems,
      isPending: isStaticNavDataPending || isDynamicNavDataPending,
    };
  }, [
    dynamicNavItems,
    initialPageSlug,
    location,
    navItems,
    slug,
    isStaticNavDataPending,
    isDynamicNavDataPending,
  ]);
};

// Custom hook for navigation actions
const useNavigationActions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { decideRedirectForSingleClose, decideRedirectForBulkClose } =
    useDecideUserRedirect();

  const deleteNavigationCustomizationMutation =
    useDeleteNavigationCustomizationMutation();

  const deleteAllCustomizationMutation =
    useDeleteAllNavigationCustomizationMutation();

  return useMemo(() => {
    const handleClick = (item: Item) => {
      if (item.href == null) {
        return;
      }

      const path = resolvePath(item.href, location.pathname);
      const replace = createPath(location) === createPath(path);

      navigate(item.href, { replace });
    };

    const handleClose = (itemId: string) => {
      deleteNavigationCustomizationMutation.mutate({ itemId });
      decideRedirectForSingleClose(itemId);
    };

    const handleCloseAll = () => {
      deleteAllCustomizationMutation.mutate(undefined, {
        onSettled: () => {
          decideRedirectForBulkClose();
        },
      });
    };

    return { handleClick, handleClose, handleCloseAll };
  }, [
    decideRedirectForBulkClose,
    decideRedirectForSingleClose,
    deleteAllCustomizationMutation,
    deleteNavigationCustomizationMutation,
    location,
    navigate,
  ]);
};

// Custom hook for redirecting the user post closing the dynamic link
const useDecideUserRedirect = () => {
  const navigate = useNavigate();
  const {
    activeDynamicNavigationItemIndex,
    dynamicNavItems,
    initialPageSlug,
    navItems,
  } = useNavigationContextData();
  const roleId = useStore((state) => state.activeUserRole?.id);

  return useMemo(() => {
    const getPath = () => {
      let path =
        dynamicNavItems[activeDynamicNavigationItemIndex ?? -1]?.originHref;
      if (!path && navItems.length > 0 && initialPageSlug) {
        path = pageWithRoleLink(roleId ?? NO_ROLE_ID, {
          pageType: 'page',
          slug: initialPageSlug,
        });
      }
      return path;
    };

    const redirectUser = () => {
      const path = getPath();
      if (path) {
        navigate(path, { replace: true });
      }
    };

    const decideRedirectForSingleClose = (itemId: string) => {
      if (
        activeDynamicNavigationItemIndex != null &&
        dynamicNavItems[activeDynamicNavigationItemIndex].id === itemId
      ) {
        redirectUser();
      }
    };

    const decideRedirectForBulkClose = () => {
      if (
        activeDynamicNavigationItemIndex != null &&
        dynamicNavItems[activeDynamicNavigationItemIndex].id
      ) {
        redirectUser();
      }
    };

    return { decideRedirectForSingleClose, decideRedirectForBulkClose };
  }, [
    activeDynamicNavigationItemIndex,
    dynamicNavItems,
    initialPageSlug,
    navItems.length,
    navigate,
    roleId,
  ]);
};

export const NavigationContextProvider: React.FC<
  NavigationContextProviderType
> = ({ children }) => {
  const navigationData = useNavigationContextData();
  const navigationActions = useNavigationActions();

  const contextValue = useMemo(
    () => ({
      ...navigationData,
      ...navigationActions,
    }),
    [navigationData, navigationActions]
  );

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error(
      'useNavigationContext must be used within a NavigationContextProvider'
    );
  }
  return context;
};
